/**
 * ArticleDetailsDialog — one dialog, two entries (R9b):
 *
 * - mode="edit"    (kebab → Edit details): Title + Alias fields.
 * - mode="publish" (kebab → Publish): Alias confirmation before every CDN
 *   publish — the alias IS the public URL, so the user sees and can change
 *   it at the moment it matters.
 *
 * Alias rules: slug-normalized live preview (the exact transform the service
 * applies on write — `normalizeArticleAlias`), uniqueness checked against the
 * project's other articles on confirm. Re-publishing under a new alias leaves
 * the previously published URL live (only `articles retire` unpublishes) — the
 * dialog says so instead of pretending otherwise.
 *
 * One dismiss affordance: footer Cancel, no header X (ui-components
 * invariant 2).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { normalizeArticleAlias, type ArticleDraft, type ArticleEditorMode } from '@modules/firebase-core';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';

export interface ArticleDetailsDialogProps {
  open: boolean;
  mode: 'edit' | 'publish';
  draft: ArticleDraft | null;
  onClose: () => void;
  /** Resolves `true` when the alias is free (excluding this draft). */
  isAliasUnique: (alias: string, excludeArticleId: string) => Promise<boolean>;
  /**
   * Fires with validated, normalized input.
   * mode="edit": persist details. mode="publish": persist (if changed) + publish.
   */
  onConfirm: (input: { title?: string; alias: string; editorMode?: ArticleEditorMode }) => Promise<void>;
  /** External busy state (e.g. publish in flight) — disables the primary. */
  busy?: boolean;
  /** Opt-in: render the "Editor mode" row (mode="edit" only). */
  showEditorMode?: boolean;
  /** Multi-screen document: Screen mode unavailable (forced-Flow guard). */
  editorModeLocked?: boolean;
}

/** Public URL preview derived from the draft's last published location. */
function previewUrl(draft: ArticleDraft | null, alias: string): string {
  const slug = alias || '{alias}';
  if (draft?.publishedUrl) {
    try {
      const url = new URL(draft.publishedUrl);
      const prefix = url.pathname.startsWith('/a/') ? '/a' : '';
      return `${url.origin}${prefix}/${slug}`;
    } catch {
      // fall through to the relative form
    }
  }
  return `/a/${slug}`;
}

/** Alias segment of the last published URL (what is actually live). */
function publishedAlias(draft: ArticleDraft | null): string | null {
  if (!draft?.publishedUrl) return null;
  try {
    const segments = new URL(draft.publishedUrl).pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? null;
  } catch {
    return null;
  }
}

export function ArticleDetailsDialog({
  open,
  mode,
  draft,
  onClose,
  isAliasUnique,
  onConfirm,
  busy = false,
  showEditorMode = false,
  editorModeLocked = false,
}: ArticleDetailsDialogProps) {
  const [title, setTitle] = useState('');
  const [alias, setAlias] = useState('');
  const [editorMode, setEditorMode] = useState<ArticleEditorMode>('screen');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Re-seed the fields each time the dialog opens for a draft.
  useEffect(() => {
    if (open && draft) {
      setTitle(draft.title);
      setAlias(draft.alias);
      setEditorMode(editorModeLocked ? 'flow' : (draft.editorMode ?? 'screen'));
      setAliasError(null);
      setSubmitting(false);
    }
  }, [open, draft, editorModeLocked]);

  const normalized = useMemo(() => normalizeArticleAlias(alias), [alias]);
  const livePublishedAlias = publishedAlias(draft);
  const showRealiasNote =
    mode === 'publish' &&
    Boolean(livePublishedAlias) &&
    livePublishedAlias !== normalized;

  const handleConfirm = async () => {
    if (!draft) return;
    if (!alias.trim()) {
      setAliasError('Alias is required');
      return;
    }
    setSubmitting(true);
    setAliasError(null);
    try {
      const unique = await isAliasUnique(normalized, draft.id);
      if (!unique) {
        setAliasError('Alias is already used by another article in this project');
        return;
      }
      await onConfirm({
        title: mode === 'edit' ? title : undefined,
        alias: normalized,
        editorMode: mode === 'edit' && showEditorMode ? editorMode : undefined,
      });
    } catch (err) {
      setAliasError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const isPublish = mode === 'publish';
  const rootTestId = isPublish ? 'article-publish-dialog' : 'article-details-dialog';

  return (
    <SDialog
      open={open}
      onClose={() => {
        if (!submitting && !busy) onClose();
      }}
      title={isPublish ? 'Publish article?' : 'Edit details'}
      maxWidth="xs"
      fullWidth
      showCloseButton={false}
      data-testid={rootTestId}
      primaryAction={{
        label: isPublish ? 'Publish' : 'Save',
        onClick: handleConfirm,
        loading: submitting || busy,
        'data-testid': isPublish ? 'article-publish-confirm-button' : 'article-details-save',
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
        disabled: submitting || busy,
      }}
    >
      <Stack spacing={2} sx={{ pt: 0.5 }}>
        {!isPublish && (
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            helperText="Shown in the articles list. A manual title stops auto-naming from content."
            data-testid="article-details-title-input"
          />
        )}
        <TextField
          label="Alias"
          value={alias}
          onChange={(e) => {
            setAlias(e.target.value);
            setAliasError(null);
          }}
          fullWidth
          size="small"
          error={Boolean(aliasError)}
          helperText={
            aliasError ?? 'Lowercase letters, digits, «-» · 3–50 chars · unique in this project'
          }
          data-testid={isPublish ? 'article-publish-alias-input' : 'article-details-alias-input'}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <LinkIcon fontSize="small" color="action" sx={{ flex: 'none' }} />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {isPublish ? 'Will publish to: ' : 'Public URL: '}
            <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {previewUrl(draft, normalized)}
            </Typography>
          </Typography>
        </Box>
        {normalized !== alias.trim() && alias.trim() !== '' && (
          <Typography variant="caption" color="text.secondary">
            Will be saved as <b>{normalized}</b>
          </Typography>
        )}
        {!isPublish && showEditorMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Editor mode
            </Typography>
            <Tooltip title={editorModeLocked ? 'Multi-screen article — edit in Flow' : ''}>
              <span style={{ display: 'inline-flex' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={editorMode}
                  onChange={(_, next: ArticleEditorMode | null) => {
                    if (next) setEditorMode(next);
                  }}
                  aria-label="Editor mode"
                  data-testid="article-details-editor-mode"
                >
                  <ToggleButton
                    value="screen"
                    disabled={editorModeLocked}
                    data-testid="article-details-editor-mode-screen"
                    sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}
                  >
                    Screen
                  </ToggleButton>
                  <ToggleButton
                    value="flow"
                    data-testid="article-details-editor-mode-flow"
                    sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}
                  >
                    Flow
                  </ToggleButton>
                </ToggleButtonGroup>
              </span>
            </Tooltip>
          </Box>
        )}
        {showRealiasNote && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            This article is currently published as <b>/{livePublishedAlias}</b> — the old URL
            stays live after publishing under the new alias.
          </Alert>
        )}
      </Stack>
    </SDialog>
  );
}

export default ArticleDetailsDialog;
